/* Copyright (c) 2020-2025 Nordcloud Oy or its affiliates. All Rights Reserved. */

import dayjs from "dayjs";
import { If, Then } from "react-if";
import styled from "styled-components";
import { Box, FlexContainer, SVGIcon, Text, theme } from "@nordcloud/gnui";
import { LastPatching, PatchingStatus } from "~/generated/graphql";
import { dateFormat } from "~/constants";
import { isNil, isNotNil } from "~/tools";
import { IconName } from "~/types/IconName";

type Props = {
  lastPatching?: LastPatching;
};

export function LastPatchingStatusContainer({ lastPatching }: Props) {
  return (
    <StyledBox boxStyle="lightGrey">
      <Text mb={theme.spacing.spacing03} weight="medium">
        Last Patching
      </Text>
      <LastPatchingStatus lastPatching={lastPatching} />
    </StyledBox>
  );
}

export function LastPatchingStatus({ lastPatching }: Props) {
  const patchingStatus = getPatchingStatus(lastPatching?.status);

  return (
    <div>
      <FlexContainer>
        <SVGIcon
          name={patchingStatus.icon}
          color={patchingStatus.color()}
          size="md"
        />
        <Text ml={theme.spacing.spacing02} tag="span">
          {patchingStatus.description}
        </Text>
      </FlexContainer>
      <If condition={isNotNil(lastPatching)}>
        <Then>
          <Text
            tag="div"
            size="sm"
            color={theme.color.text.text02}
            paddingLeft={theme.spacing.spacing07}
          >
            {dayjs(lastPatching?.date).format(
              dateFormat.dayMonthYearHourMinute
            )}
          </Text>
        </Then>
      </If>
    </div>
  );
}

type Status = {
  description: string;
  icon: IconName;
  color: () => string;
};

function getPatchingStatus(status?: PatchingStatus): Status {
  if (isNil(status)) {
    return {
      description: "No status",
      icon: "warningFilled",
      color: () => `${theme.color.background.warning}`,
    };
  }

  switch (status) {
    case PatchingStatus.Error:
      return {
        description: "Error",
        icon: "errorFilled",
        color: () => `${theme.color.background.error}`,
      };
    case PatchingStatus.Failed:
      return {
        description: "Failed",
        icon: "cancelFilled",
        color: () => `${theme.color.background.error}`,
      };
    case PatchingStatus.NotInTimeWindow:
      return {
        description: "Not In Time Window",
        icon: "timeRangeFilled",
        color: () => `${theme.color.background.warning}`,
      };
    case PatchingStatus.Timeout:
      return {
        description: "Timeout",
        icon: "timeOutFilled",
        color: () => `${theme.color.background.warning}`,
      };
    case PatchingStatus.Success:
      return {
        description: "Successful",
        icon: "successFilled",
        color: () => `${theme.color.background.success}`,
      };
    default:
      return {
        description: "Unknown",
        icon: "warningFilled",
        color: () => `${theme.color.background.warning}`,
      };
  }
}

const StyledBox = styled(Box)`
  flex: 1;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  margin-top: -0.0625rem;
`;
